import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';


const UpgradeModalContext = createContext({});

export const useUpgradeModal = () => useContext(UpgradeModalContext);

export const UpgradeModalProvider = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [requiredPlan, setRequiredPlan] = useState('');

  const show = useCallback((plan) => {
    setRequiredPlan(plan || 'PRO');
    setVisible(true);
  }, []);

  const hide = useCallback(() => {
    setVisible(false);
  }, []);

  // Patch global fetch to intercept 403 UPGRADE_REQUIRED
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response?.status === 403) {
        // Clone so we can read body without consuming it
        const cloned = response?.clone();
        try {
          const json = await cloned?.json();
          if (json?.error === 'UPGRADE_REQUIRED') {
            show(json?.required_plan);
          }
        } catch (_) {
          // Not JSON or no body — ignore
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [show]);

  return (
    <UpgradeModalContext.Provider value={{ show, hide }}>
      {children}
      {visible && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: "'Courier New', Courier, monospace",
          }}
        >
          <div
            style={{
              backgroundColor: '#0a0a0a',
              border: '1px solid #1a1a1a',
              borderTop: '2px solid #14b8a6',
              padding: '48px 40px',
              maxWidth: '480px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 0 60px rgba(20,184,166,0.15)',
            }}
          >
            {/* Icon */}
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  border: '2px solid #14b8a6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  color: '#14b8a6',
                  fontSize: '24px',
                }}
              >
                ⬆
              </div>
            </div>

            {/* Title */}
            <div
              style={{
                fontSize: '22px',
                fontWeight: 700,
                color: '#f0f0f0',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                marginBottom: '12px',
              }}
            >
              UPGRADE YOUR PLAN
            </div>

            {/* Subtitle */}
            <div
              style={{
                fontSize: '13px',
                color: '#888',
                letterSpacing: '0.05em',
                marginBottom: '36px',
                lineHeight: '1.6',
              }}
            >
              This feature requires the{' '}
              <span style={{ color: '#14b8a6', fontWeight: 600 }}>
                {requiredPlan}
              </span>{' '}
              plan
            </div>

            {/* Divider */}
            <div
              style={{
                height: '1px',
                backgroundColor: '#1a1a1a',
                marginBottom: '32px',
              }}
            />

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a
                href="/pricing"
                onClick={hide}
                style={{
                  display: 'block',
                  padding: '14px 24px',
                  backgroundColor: '#14b8a6',
                  color: '#000',
                  fontWeight: 700,
                  fontSize: '13px',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0d9488')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#14b8a6')}
              >
                UPGRADE NOW
              </a>

              <button
                onClick={hide}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '14px 24px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  fontWeight: 600,
                  fontSize: '13px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  border: '1px solid #2a2a2a',
                  cursor: 'pointer',
                  transition: 'color 0.2s, border-color 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#999';
                  e.currentTarget.style.borderColor = '#444';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '#666';
                  e.currentTarget.style.borderColor = '#2a2a2a';
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </UpgradeModalContext.Provider>
  );
};

export default UpgradeModalProvider;
